<script setup>
import { computed, nextTick, onMounted, ref } from "vue"
import { useDisplay } from "vuetify"
import { useAppStore } from "@/stores/appStore"
import { useAuthStore } from "@/stores/authStore"
import Swal from "sweetalert2"
import { enums as LEVEL } from "@/config/enums/ProgramLevel"
import { contentTabsBachelors, contentTabsEligiblePrograms, contentTabsMaster } from "@/utils/content"
import { router } from "@/plugins/1.router"
import { useRoute } from "vue-router"
import { login as loginApi } from "@/services/ApiUrlService"
import ForgotPassword from "@/views/ForgotPassword.vue"

definePage({
  name: "index",
  meta: {
    layout: "new-home",
    unauthenticatedOnly: true,
  },
})

const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR
const logoGlobalSurvey = new URL("@images/logo-global-survey.png", import.meta.url).href
const iconPalme = "/images/logo.png"
const isDialogVisibleMoreInfo = ref(false)
const isDialogVisibleDbaMethodology = ref(false)
const typePopup = ref(null)

const route = useRoute()
const authStore = useAuthStore()
const { smAndDown } = useDisplay()

const isSchoolLoginVisible = ref(false)
const isPasswordVisible = ref(false)
const loadingLogin = ref(false)
const refLoginForm = ref()
const credentialsLogin = ref({ username: "", password: "" })
const loginErrors = ref({ username: null, password: null })
const loginErrorMessage = ref("")

const loginUser = async () => {
  loadingLogin.value = true
  loginErrorMessage.value = ""
  try {
    const response = await $api(loginApi, {
      method: "POST",
      body: {
        username: cleanEmail(credentialsLogin.value.username),
        password: credentialsLogin.value.password,
      },
      loading: true,
      noPushMessage: true,
    })
    authStore.login(response, route)
  } catch {
    loginErrorMessage.value = "Login information is incorrect. Please check your credentials and try again."
  } finally {
    loadingLogin.value = false
  }
}

const onSubmitLogin = () => {
  refLoginForm.value?.validate().then(({ valid }) => {
    if (valid) loginUser()
  })
}

const actorCards = [
  {
    title: "Schools",
    description: "Submit information about your programs",
    icon: "tabler-building-arch",
    type: "school",
  },
  {
    title: "Students",
    description: "Share your academic experience",
    icon: "tabler-school",
    type: "student",
  },
  {
    title: "Companies",
    description: "Evaluate programs and graduate profiles (Masters & MBA programs only)",
    icon: "tabler-briefcase-2",
    type: "company",
  },
]
const methodologyActors = [
  {
    title: "HR Professionals",
    description: "Evaluating the employability and market value of graduates.",
    icon: "tabler-briefcase",
  },
  {
    title: "Higher Education Institutions",
    description: "Providing detailed data on program structure and academic quality.",
    icon: "tabler-building",
  },
  {
    title: "The Students",
    description: "Sharing direct feedback on their learning experience and satisfaction.",
    icon: "tabler-school",
  },
]
const methodologyCriteria = [
  {
    title: "Program Notoriety",
    description: "The reputation and recognition of the program within its field",
    icon: "tabler-star",
  },
  {
    title: "Career Opportunities",
    description: "First job salary and professional prospects upon completion.",
    icon: "tabler-trending-up",
  },
  {
    title: "Student Satisfaction",
    description: "Direct feedback from students on the quality of the program.",
    icon: "tabler-mood-smile",
  },
]
const rankingCards = [
  {
    title: "Eligible Programs",
    description:
      "The Eduniversal survey welcomes the following programs, regardless of specialization or scope (national or international):",
    icon: "tabler-book",
    popupType: 'EP',
  },
  {
    title: "Fields of Study in the Ranking",
    description:
      "of study across 10 categories are open to competition in the Eduniversal ranking.",
    icon: "tabler-world",
    popupType: "MM",
  },
]

const programMajor = computed(() => useAppStore().getList("getProgramMajorInUse"))

const mmFilteredPrograms = computed(() => {
  if (!programMajor.value) return []
  const hasSpecialities = item => Array.isArray(item.programSpecialities) && item.programSpecialities.length > 0
  const specPrixList = [1, 14, 38, 39, 40, 44]

  return programMajor.value
    .filter(item => !specPrixList.includes(item.id))
    .filter(hasSpecialities)
})

const mmProgramCount = computed(() =>
  mmFilteredPrograms.value.reduce((sum, item) => sum + item.programSpecialities.length, 0),
)

const mmCategoryCount = computed(() => mmFilteredPrograms.value.length)

const majorProgramCondition = computed(() => {
  const survey = typePopup.value
  if (!programMajor.value)
    return []

  const hasSpecialities = item => Array.isArray(item.programSpecialities) && item.programSpecialities.length > 0
  const specPrixList = [1, 14, 38, 39, 40, 44]
  if (survey === "MM") {
    return programMajor.value
      .filter(item => !specPrixList.includes(item.id))
      .filter(hasSpecialities)
  }
  if (survey === "MB") {
    return programMajor.value
      .filter(item => !specPrixList.includes(item.id))
      .filter(item => [LEVEL.MB].includes(item.programLevel?.id))
      .filter(hasSpecialities)
  }

  return []
})

function openRankingPopup(type, event) {
  if (!type) return
  event?.preventDefault()
  typePopup.value = type
  isDialogVisibleMoreInfo.value = true
}
function openPopupType(type) {
  if (type === "school") {
    isSchoolLoginVisible.value = true
  }
  else if (type === "company") {
    Swal.fire({
      imageUrl: iconPalme,
      imageWidth: 180,
      imageAlt: 'Eduniversal Global Survey',
      html: `
        <p style="font-size: 14px; font-weight: normal; font-style: normal;" class="text-black text-start">
          Your login credentials have already been sent to you by email from:
          <a class="pointer text-sky" href="mailto:mastersurvey@eduniversal-group.com"><u>mastersurvey@eduniversal-group.com</u></a>
        </p>
        <p style="font-size: 14px; font-weight: normal; font-style: normal;" class="text-black text-justify">
          If you cannot locate this email, please check your spam or junk folder. <br />If needed, contact us for assistance.
        </p>
      `,
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      position: "center",
    })
  }
  else if (type === "student") {
    Swal.fire({
      imageUrl: iconPalme,
      imageWidth: 180,
      imageAlt: 'Eduniversal Global Survey',
      html: `
        <p style="font-size: 14px; font-weight: normal; font-style: normal;" class="text-black text-start">
          Your login credentials have already been sent to you by email from:
          <a class="pointer text-sky" href="mailto:mastersurvey@eduniversal-group.com"><u>mastersurvey@eduniversal-group.com</u></a>
        </p>
        <p style="font-size: 14px; font-weight: normal; font-style: normal;" class="text-black text-justify">
          If you cannot locate this email, please check your spam or junk folder.<br /> If needed, contact us for assistance.
        </p>
      `,
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      position: "center",
    })
  }
  else {
    Swal.fire({
      imageUrl: iconPalme,
      imageWidth: 180,
      imageAlt: 'Eduniversal Global Survey',
      html: `
        <p style="font-size: 14px; font-weight: normal; font-style: normal;" class="text-error text-center">
          No Data Available
        </p>
      `,
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      position: "center",
    })
  }
}
function redirectPage(url) {
  window.open(url, "_blank")
}

onMounted(async () => {
  if (route.query.openLogin === "1") {
    setTimeout(() => {
      isSchoolLoginVisible.value = true
    }, 700)
    const { openLogin, ...restQuery } = route.query
    router.replace({ query: restQuery })
  }
})
</script>

<template>
  <section
    id="login"
    class="hero"
  >
    <div class="hero__overlay">
      <div class="home-page__container hero__content">
        <h1>Eduniversal Programs<br class="d-md-none d-block"> Survey {{ +CURRENT_YEAR }}</h1>
        <h2>
          {{ +CURRENT_YEAR + 1 }} Eduniversal Ranking of the <br class="d-md-none d-block"> Best Masters, MBA & DBA <br>
        </h2>
        <p>
          Provide your input and help evaluate academic programs.
          <br class="d-none d-md-block">
          Select your profile to access your dedicated survey space.
        </p>
        <RouterLink
          to=""
          class="hero__cta"
        >
          Deadline: 28 November 2026
        </RouterLink>

        <div class="hero__cards">
          <VCard
            v-for="card in actorCards"
            :key="card.title"
            class="hero-card"
            :elevation="0"
            @click="openPopupType(card.type)"
          >
            <div class="hero-card__icon">
              <VIcon
                :icon="card.icon"
                size="22"
              />
            </div>
            <h3>{{ card.title }}</h3>
            <p>{{ card.description }}</p>
            <button
              class="mt-5 font-weight-bold"
              type="button"
            >
              Access Survey >
            </button>
          </VCard>
        </div>
      </div>
    </div>
  </section>

  <section
    id="methodology"
    class="section section--methodology"
  >
    <div class="home-page__container">
      <h2 class="section-title">
        How Eduniversal Program Rankings Are Built?
      </h2>
      <p class="section-subtitle">
        Eduniversal rankings are based on a comprehensive and internationally<br class="d-none d-md-block"> recognized methodology that combines institutional reputation, programme<br class="d-none d-md-block"> performance indicators, and students satisfaction to deliver relevant,<br class="d-none d-md-block"> transparent, and credible rankings worldwide.
      </p>
      <div class="methodology-grid">
        <article class="methodology-card">
          <h3 class="methodology-card__heading">
            <VIcon
              icon="tabler-users"
              size="36"
              color="#969494"
            />
            Masters & MBAs Ranking Methodology
          </h3>
          <p class="text-justify" style="font-size: 16px;">
            The Eduniversal ranking of the Best Master's & MBA programs is built on a transparent and rigorous methodology, combining the perspectives of three key actors and measuring performance across three core criteria: programme reputation, career opportunities, and student satisfaction.
          </p>
          <button
              type="button"
              class="ranking-card__link"
              @click="redirectPage('https://www.best-masters.com/methodology-methodology.html')"
          >
            Learn More >
          </button>
        </article>
        <article class="methodology-card">
          <h3 class="methodology-card__heading">
            <VIcon
              icon="tabler-award"
              size="36"
              color="#969494"
            />
            DBA Ranking Methodology
          </h3>
          <p class="text-justify" style="font-size: 16px;">
            To compile its ranking of the World's Best DBA programmes, Eduniversal evaluates each programme across three criteria: institutional reputation, research quality and programme rigour, and candidate and graduate satisfaction, drawing on data provided by programme directors and direct feedback from DBA candidates and graduates.
          </p>
          <button
              type="button"
              class="ranking-card__link"
              @click="isDialogVisibleDbaMethodology = true"
          >
            Learn More >
          </button>
        </article>
      </div>
    </div>
  </section>

  <section
    id="ranking"
    class="section section--white section--ranking"
  >
    <div class="home-page__container">
      <h2 class="section-title">
        Programs & Ranking Categories
      </h2>
      <p class="section-subtitle section-subtitle--ranking">
        Discover how Eduniversal structures its rankings to reflect the diversity and evolution of global higher education.
      </p>
      <div class="ranking-grid">
        <article
          v-for="item in rankingCards"
          :key="item.title"
          class="ranking-card"
        >
          <div class="ranking-card__icon">
            <VIcon
              :icon="item.icon"
              size="22"
              class="ranking-card__vicon"
            />
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.popupType === 'MM' ? `${mmProgramCount} fields of study across ${mmCategoryCount} categories are open to competition in the Eduniversal ranking.` : item.description }} <a class="cursor-pointer font-weight-bold button-learn" @click="openRankingPopup(item.popupType)">Learn More ></a></p>
        </article>
      </div>
    </div>
  </section>

  <DbaMethodology v-model:isDialogVisibleDbaMethodology="isDialogVisibleDbaMethodology" />

  <MoreInfo
    v-model:isDialogVisibleMoreInfo="isDialogVisibleMoreInfo"
    :data="typePopup === 'MB' ? contentTabsBachelors : typePopup === 'MM' ? contentTabsMaster : typePopup === 'EP' ? contentTabsEligiblePrograms : {}"
    :data-spec="majorProgramCondition"
  />

  <VDialog
    v-model="isSchoolLoginVisible"
    :max-width="smAndDown ? '95vw' : '480px'"
  >
    <VCard style="border-radius: 12px; padding: 8px;">
      <DialogCloseBtn @click="isSchoolLoginVisible = false" />
      <VCardText class="px-2 px-md-5">
        <div class="d-flex justify-center mb-4">
          <img
            :src="iconPalme"
            alt="Eduniversal"
            width="180"
          >
        </div>
        <h3 class="mb-4 text-center font-bl" style="font-size:18px;font-weight:700;">
          Universities & Schools Login
        </h3>
        <VForm ref="refLoginForm">
          <div class="d-flex flex-column gap-3">
            <AppTextField
              v-model="credentialsLogin.username"
              autofocus
              type="text"
              prepend-inner-icon="tabler-user"
              placeholder="Username / Email address"
              :rules="[requiredValidator, emailValidator(credentialsLogin.username, 'Invalid email format')]"
              @blur="credentialsLogin.username = cleanEmail(credentialsLogin.username)"
              :error-messages="loginErrors.username"
            />
            <AppTextField
              v-model="credentialsLogin.password"
              placeholder="Password"
              :type="isPasswordVisible ? 'text' : 'password'"
              :rules="[requiredValidator]"
              :error-messages="loginErrors.password"
            >
              <template #prepend-inner>
                <VIcon icon="tabler-lock" color="black" />
              </template>
              <template #append-inner>
                <VIcon
                  class="pointer"
                  :icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  size="20"
                  color="black"
                  @click="isPasswordVisible = !isPasswordVisible"
                />
              </template>
            </AppTextField>
          </div>
          <VAlert
            v-if="loginErrorMessage"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3 mb-1"
          >
            {{ loginErrorMessage }}
          </VAlert>
          <div class="d-flex align-center justify-space-between mt-0 mt-md-4 mb-4 layout-forgot-password">
            <!-- style="background:#000;color:#fff;border-radius:8px;text-transform:uppercase;letter-spacing:0.05em;" -->
            <SubmitBtn
              color="black"
              :loading="loadingLogin"
              @click="onSubmitLogin"
            >
              {{ $gettext('Log in') }}
            </SubmitBtn>
            <ForgotPassword isNewLogin />
          </div>
          <VDivider class="mb-4" />
          <div class="layout-forgot">
            <h3 style="font-size:15px;font-weight:700;">
              {{ $gettext('Not registered yet?') }}
            </h3>
            <VBtn
              variant="outlined"
              color="#222"
              style="border-radius:8px;text-transform:none;"
              @click="router.push('/inscription')"
            >
              {{ $gettext('Create your account') }}
            </VBtn>
          </div>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.layout-forgot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  @media (max-width: 460px) {
    flex-direction: column;
    align-items: center;
  }
}
.layout-forgot-password {
  @media (max-width: 959px) {
    flex-direction: column-reverse;
    align-items: center;
    gap: 6px;

    > :last-child {
      align-self: flex-end;
    }
  }

}

.button-learn {
  transition: all .3s linear;
  color: rgba(var(--v-theme-black));

  &:hover {
    color: rgba(var(--v-theme-second-primary));
  }
}
</style>
